import { manualDiscountVariantFields, type ManualDiscount } from "@/lib/manualDiscount";
import { getVariantPrice, resolveSelectionPrice, sellableSelectionPrice } from "@/lib/pricing";
import type { ProductVariation } from "@/lib/products";
import type { CustomerGroup } from "@/lib/reseller-groups";

// Snapshot harga jual ke order_items.unit_price — SUMBER RUMUS TUNGGAL = pricing.ts.
// Helper di file ini MURNI (tanpa Supabase) supaya bisa diuji & dipakai bersama
// oleh: re-snapshot saat edit OPEN PO / simpan harga produk (data layer browser),
// dan route backfill admin (server). SQL tidak pernah menghitung diskon — hanya
// menerima/mewarisi unit_price (lihat mig 128 & CLAUDE.md "model harga").

/** Baris varian dengan field harga (bentuk yang dipahami getVariantPrice). */
export type VariantPricing = {
  variantId: string;
  variantName: string;
  pricing: ProductVariation;
  /**
   * `product_variants.is_active` — terisi bila baris DB membawa kolomnya.
   * `false` = varian DIMATIKAN admin: ia tetap bisa dicocokkan ke pesanan
   * lamanya, tapi tidak ikut menentukan harga produk (perwakilan / seragam-
   * tidaknya) — cermin checkout, yang hanya melihat varian aktif (RLS pembeli).
   * Absen = dianggap aktif.
   */
  isActive?: boolean;
  /**
   * Merek + Non Web produk varian ini (diskon merek per level, mig 7070) —
   * terisi bila baris DB membawa embed `products(brand, is_manual)`. Absen =
   * tak diketahui: jalur yang butuh aturan merek melewati barisnya
   * (`levelForKnownProduct`), bukan menebak.
   */
  product?: { brand: string | null; isManual: boolean };
};

/** Embed `products(brand, is_manual)` pada baris varian (PostgREST: objek atau larik). */
type VariantProductEmbed = { brand?: string | null; is_manual?: boolean | null };

/** Identitas satu order_item yang akan di-snapshot harganya. */
export type SnapshotTarget = {
  variantId: string | null;
  variantName: string;
};

function numOrUndefined(
  value: number | string | null | undefined
): number | undefined {
  if (value === null || value === undefined || value === "") return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Cari varian yang cocok untuk satu order_item: utamakan match `variantId`,
 * fallback ke nama varian (snapshot lama bisa tak ber-variant_id, mis. impor).
 */
export function matchVariant(
  target: SnapshotTarget,
  variants: VariantPricing[]
): VariantPricing | null {
  if (target.variantId) {
    const byId = variants.find((v) => v.variantId === target.variantId);
    if (byId) return byId;
  }
  return variants.find((v) => v.variantName === target.variantName) ?? null;
}

/**
 * Harga satuan (integer) hasil snapshot untuk satu order_item via pricing.ts.
 * Mengembalikan `null` bila varian tak ditemukan ATAU tak ada harga yang sah
 * untuk kolom uang (harga menyusul) — pemanggil membiarkan unit_price NULL.
 *
 * ATURAN HARGA = CHECKOUT (`buildCheckoutLineResolver`, data.ts):
 * `resolveSelectionPrice` + `sellableSelectionPrice`. Varian yang dipesan
 * dulu; tak berharga sendiri → varian PERWAKILAN bila harga varian produknya
 * SERAGAM; tak ada varian berharga → `productPriceNormal`
 * (`products.price_normal`, lapis terakhir). Dulu jalur ini hanya membaca
 * harga varian (`getVariantPrice`), sehingga produk yang harganya baru diisi
 * di sebagian varian / di level produk tak pernah mengisi pesanan harga
 * menyusulnya — bug live galea 2026-10-19 "Ivara Dress": 224 varian, 2
 * berharga 167.000, pesanan Agen tetap NULL padahal notanya menagih 133.600.
 *
 * Harga varian CAMPURAN tetap HARGA MENYUSUL (`unpricedVariant`, natasyashop
 * #2095): menagihkan harga varian perwakilan pada produk berharga campuran
 * adalah bug uang, jadi hasilnya `null`, bukan angka perwakilan.
 *
 * Varian NONAKTIF (`isActive === false`): perwakilan diambil dari varian
 * AKTIF lebih dulu — yang dilihat pembeli di checkout (RLS pembeli) — tapi
 * seragam-tidaknya dinilai dari SEMUA varian. Sengaja lebih ketat dari
 * checkout: kalau hanya varian aktif yang dinilai, produk yang SEMUA variannya
 * dimatikan (harga campuran) terbaca "tanpa varian berharga" dan
 * `products.price_normal` (MIN) ikut ditagihkan — kelas #2095. Arah gagalnya
 * harga menyusul, bukan angka varian lain. Pencocokan varian pesanan memakai
 * semua varian (pesanan lama bisa menunjuk varian yang sudah dimatikan).
 *
 * SUMBU HARGA (mig 7680, putusan 2026-10-20 #1429): `priceAxis` true/null
 * (produk ber-`price_affecting_groups`, atau tak diketahui) → varian tanpa
 * harga sendiri TIDAK ditebak dari keseragaman varian lain maupun harga
 * produk — cermin checkout. Warisan slot (`preorder_inherited_pricing`) tak
 * dihitung di sini, jadi baris seperti itu tetap HARGA MENYUSUL (arah aman;
 * checkout yang menghitung warisannya). Absen = aturan lama, hanya untuk
 * pemanggil yang tak pernah menyangkut baris tanpa harga sendiri.
 *
 * SENGAJA TIDAK SADAR-PROMO (mig 3470). Jalur ini hanya mengisi `unit_price`
 * yang NULL/0, yaitu item "harga menyusul". Pada saat pembeli memesannya produk
 * itu BELUM punya harga — promo mustahil berlaku (promo memotong sebuah angka;
 * tak ada angkanya). Menerapkan promo yang kebetulan berjalan hari ini akan
 * MENGARANG potongan yang tak pernah dilihat pembeli, lalu MENGUNCINYA
 * selamanya karena unit_price beku. Harga promo hanya boleh masuk lewat
 * checkout, tempat pembeli benar-benar melihat angkanya.
 */
export type ResolvedUnitPrice = {
  /** `order_items.unit_price` — harga final per pcs (SUDAH memuat tambahan). */
  unitPrice: number;
  /** `order_items.unit_price_extra` — tambahan yang termasuk di `unitPrice` (mig 7900). */
  priceExtra: number;
};

/**
 * Harga potret + bagian tambahannya (mig 7900) — untuk PENULIS `order_items`
 * yang harus menulis `unit_price` DAN `unit_price_extra` berpasangan
 * (resnapshot, route backfill, Perbaiki Harga). `resolveSnapshotUnitPrice` di
 * bawahnya = angkanya saja (bentuk lama; identik untuk varian tanpa tambahan).
 */
export function resolveSnapshotPriceParts(
  target: SnapshotTarget,
  variants: VariantPricing[],
  group: CustomerGroup,
  productPriceNormal?: number,
  priceAxis?: boolean | null
): ResolvedUnitPrice | null {
  const match = matchVariant(target, variants);
  if (!match) return null;
  // Urutan stabil aktif-dulu: `resolveSelectionPrice` memilih perwakilan dari
  // varian berharga PERTAMA, dan menilai keseragaman atas seluruh daftar.
  const ordered = [
    ...variants.filter((v) => v.isActive !== false),
    ...variants.filter((v) => v.isActive === false)
  ];
  const selection = resolveSelectionPrice(
    match.pricing,
    {
      variations: ordered.map((v) => v.pricing),
      priceNormal: productPriceNormal,
      priceAxis
    },
    group
  );
  const { price, priceExtra } = sellableSelectionPrice(selection);
  return price === undefined ? null : { unitPrice: price, priceExtra };
}

export function resolveSnapshotUnitPrice(
  target: SnapshotTarget,
  variants: VariantPricing[],
  group: CustomerGroup,
  productPriceNormal?: number,
  priceAxis?: boolean | null
): number | null {
  return resolveSnapshotPriceParts(target, variants, group, productPriceNormal, priceAxis)?.unitPrice ?? null;
}

/**
 * `products.price_affecting_groups` → produk ber-SUMBU harga? (mig 7680).
 * Kolomnya sengaja tak terbaca anon; jalur admin membacanya langsung.
 */
export function hasPriceAxis(priceAffectingGroups: unknown): boolean {
  return Array.isArray(priceAffectingGroups) && priceAffectingGroups.length > 0;
}

/**
 * Bentuk CustomerGroup dari baris DB `reseller_groups` (atau null = Customer
 * biasa tanpa diskon). Grup nonaktif diperlakukan sebagai null — cermin resolusi
 * `useCustomerGroup` di storefront. Dicermin SQL `promo_customer_slot` (mig 7080,
 * slot kelayakan promo di gerbang lantai harga) — kalau satu berubah, keduanya.
 */
export function groupFromRow(
  row:
    | {
        id: string;
        slot: number | string;
        name: string;
        discount_persen: number | string | null;
        is_active: boolean;
      }
    | null
    | undefined
): CustomerGroup {
  if (!row || row.is_active === false) return null;
  return {
    id: row.id,
    slot: Number(row.slot),
    name: row.name,
    discountPersen: Number(row.discount_persen ?? 0),
    isActive: true
  };
}

/**
 * Bentuk ProductVariation (untuk getVariantPrice) dari baris DB
 * `product_variants`. Mengoersi numeric Postgres (bisa string lewat PostgREST)
 * agar aritmetika harga konsisten.
 */
export function variantPricingFromRow(row: {
  id: string;
  name: string;
  sku?: string | null;
  option_ids?: string[] | null;
  price_normal?: number | string | null;
  price_extra?: number | string | null;
  diskon_mode?: string | null;
  diskon_reseller?: number | string | null;
  diskon_agen?: number | string | null;
  diskon_slot3?: number | string | null;
  diskon_slot4?: number | string | null;
  diskon_slot5?: number | string | null;
  diskon_slot6?: number | string | null;
  diskon_slot7?: number | string | null;
  diskon_slot8?: number | string | null;
  diskon_slot9?: number | string | null;
  diskon_slot10?: number | string | null;
  diskon_slot11?: number | string | null;
  diskon_slot12?: number | string | null;
  diskon_slot13?: number | string | null;
  diskon_slot14?: number | string | null;
  diskon_slot15?: number | string | null;
  is_active?: boolean | null;
  products?: VariantProductEmbed | VariantProductEmbed[] | null;
}): VariantPricing {
  const embed = Array.isArray(row.products) ? row.products[0] : row.products;
  return {
    ...(embed
      ? { product: { brand: embed.brand ?? null, isManual: embed.is_manual === true } }
      : {}),
    // Hanya bila kolomnya dimuat — absen tetap "dianggap aktif".
    ...(row.is_active === undefined || row.is_active === null
      ? {}
      : { isActive: row.is_active !== false }),
    variantId: row.id,
    variantName: row.name,
    pricing: {
      id: row.id,
      name: row.name,
      sku: row.sku ?? row.name,
      optionIds: row.option_ids ?? [],
      price_normal: numOrUndefined(row.price_normal),
      price_extra: numOrUndefined(row.price_extra),
      diskonMode:
        row.diskon_mode === "nominal" || row.diskon_mode === "persen"
          ? row.diskon_mode
          : undefined,
      diskonReseller: numOrUndefined(row.diskon_reseller),
      diskonAgen: numOrUndefined(row.diskon_agen),
      diskonSlot3: numOrUndefined(row.diskon_slot3),
      diskonSlot4: numOrUndefined(row.diskon_slot4),
      diskonSlot5: numOrUndefined(row.diskon_slot5),
      diskonSlot6: numOrUndefined(row.diskon_slot6),
      diskonSlot7: numOrUndefined(row.diskon_slot7),
      diskonSlot8: numOrUndefined(row.diskon_slot8),
      diskonSlot9: numOrUndefined(row.diskon_slot9),
      diskonSlot10: numOrUndefined(row.diskon_slot10),
      diskonSlot11: numOrUndefined(row.diskon_slot11),
      diskonSlot12: numOrUndefined(row.diskon_slot12),
      diskonSlot13: numOrUndefined(row.diskon_slot13),
      diskonSlot14: numOrUndefined(row.diskon_slot14),
      diskonSlot15: numOrUndefined(row.diskon_slot15)
    }
  };
}

/**
 * Varian dengan harga normal diganti Harga BARIS (`order_items.
 * manual_price_normal`, mig 6390). Produk Non Web ber-SKU bersama boleh
 * ber-Harga beda per reseller (putusan pemilik, bughunt D8-2(b)), sedangkan
 * `price_normal` varian hanya memegang Harga reseller PERTAMA — tanpa
 * penggantian ini harga level pembeli lain dihitung dari Harga orang lain,
 * dan banner "Perbaiki Harga" / backfill `mispriced` "mengoreksi" Harga
 * reseller yang lebih tinggi turun. `rowPrice` null/tak sah (produk katalog,
 * baris sebelum mig 6390) = varian apa adanya. Diskon varian tetap berlaku:
 * diskon milik PRODUK.
 */
export function withRowNormalPrice(
  variant: VariantPricing,
  rowPrice: number | string | null | undefined
): VariantPricing {
  const price = numOrUndefined(rowPrice);
  if (price === undefined || price < 0) return variant;
  return { ...variant, pricing: { ...variant.pricing, price_normal: price } };
}

/** True bila unit_price tersimpan dianggap "kosong" (NULL / 0) → perlu diisi. */
export function isEmptyUnitPrice(value: number | string | null | undefined): boolean {
  return value === null || value === undefined || Number(value) === 0;
}

/**
 * Pelanggan yang grup resellernya PERLU dimuat sebelum re-snapshot
 * (`resnapshotOrderItemsForProduct`): pemilik order yang punya minimal satu
 * baris yang mungkin dihargai ulang. Uji nyata K7 (2026-10-30): dulu grup
 * dimuat untuk SETIAP pemesan, satu request berurutan per orang, padahal pada
 * Simpan biasa tak ada baris yang kosong — produk 11 pembeli = 11 request
 * (±8 dari 18 detik Simpan).
 *
 * Sengaja SUPERSET dari baris yang benar-benar dihargai: saringan voucher,
 * harga manual, bundel, dan hadiah tetap satu-satunya di loop re-snapshot.
 * Lebih = satu id lebih di kueri batch yang sama; kurang dijaga pemanggil
 * (pelanggan yang tak dimuat → barisnya dilewati, bukan dihargai tanpa grup).
 */
export function customersNeedingGroup(
  orders: ReadonlyArray<{
    customer_id?: string | null;
    order_items?: ReadonlyArray<{ unit_price?: number | string | null }> | null;
  }>,
  onlyNullOrZero: boolean
): string[] {
  const ids = new Set<string>();
  for (const order of orders) {
    const customerId = order.customer_id ?? null;
    if (!customerId || ids.has(customerId)) continue;
    const items = order.order_items ?? [];
    if (items.some((item) => !onlyNullOrZero || isEmptyUnitPrice(item.unit_price))) {
      ids.add(customerId);
    }
  }
  return [...ids];
}

// ─── Cakupan backfill (route /api/admin/backfill-unit-price) ─────────────────
//   'empty'     → isi baris NULL/0 (perilaku asli, idempoten)
//   'mispriced' → betulkan baris yang TERLANJUR ber-harga PENUH karena importer
//                 lama men-snapshot price_normal mentah (bug 2026-08)
export type BackfillScope = "empty" | "mispriced";

/** `scope` dari query/body — nilai tak dikenal jatuh ke perilaku lama. */
export function parseBackfillScope(value: unknown): BackfillScope {
  return value === "mispriced" ? "mispriced" : "empty";
}

/** Status nota yang berarti tagihannya HIDUP (bukan void/rejected). */
export const ACTIVE_INVOICE_STATUSES = ["pending_acc", "open", "partial", "paid"] as const;

/** Satu baris nota `product_item` AKTIF (status di atas, tak dibatalkan) milik sebuah order_item. */
export type BilledLine = {
  qty: number | string;
  amount: number | string;
  /**
   * Potret tambahan per pcs baris nota (`invoice_lines.unit_price_extra`, mig
   * 7900); NULL = 0 (pra-7900). ABSEN = pemanggil tak membacanya → tak dinilai.
   */
  unitPriceExtra?: number | string | null;
};

/**
 * Apakah mengisi `unit_price = resolved` SEJALAN dengan nota yang sudah
 * menagih baris ini: SETIAP baris nota aktifnya menagih tepat `resolved × qty`.
 * Tanpa baris nota → true (belum ada yang bisa dibantah).
 *
 * Kenapa perlu (putusan pemilik 2026-10-20, opsi 1): baris "harga menyusul"
 * SERING sudah dinotakan — admin mengetik nominalnya saat Buat Nota, dan RPC
 * nota tidak menulis balik `unit_price`. Mengisi `unit_price` dari katalog di
 * bawah nota yang menagih angka LAIN melahirkan dua harga untuk barang yang
 * sama (pesanan vs nota) di kolom yang dibaca DP, Tandai Lunas, dan laporan.
 * PROD 2026-10-20: "Oliver Set" nota LUNAS Rp 0 sementara variannya kini
 * 180.000; "Ivara Dress" nota 133.600 = harga katalog → boleh diisi.
 *
 * Mengisi DARI NOTA (amount ÷ qty) sengaja TIDAK dilakukan: `unit_price`
 * wajib lahir dari `pricing.ts`, dan nominal nota bisa memuat nego/voucher.
 * Baris yang berselisih dibiarkan NULL dan dilaporkan.
 *
 * `resolvedExtra` (bughunt D14-32): tambahan yang termasuk di `resolved`. Nota
 * yang nominalnya SAMA tapi memecahnya jadi dasar/tambahan BERBEDA (mis. nota
 * terbit dengan tambahan 0, katalog kini 80.000 + 20.000) juga berselisih —
 * menulis potret pesanan baru di bawahnya melahirkan dua "Harga Umum" untuk
 * barang yang sama (`price_extra_lint()` mig 8050 menangkapnya). Absen = cek
 * nominal saja (perilaku lama).
 */
export function billedLinesAgree(
  resolved: number,
  lines: readonly BilledLine[],
  resolvedExtra?: number
): boolean {
  return lines.every((line) => {
    const qty = Number(line.qty);
    const amount = Number(line.amount);
    if (!(Number.isFinite(qty) && Number.isFinite(amount) && amount === resolved * qty)) return false;
    if (resolvedExtra === undefined || line.unitPriceExtra === undefined) return true;
    return Number(line.unitPriceExtra ?? 0) === resolvedExtra;
  });
}

/** Baris order_item yang sudah diresolusi harganya + status kuncinya. */
export type BackfillCandidate = {
  unitPrice: number | string | null;
  resolved: number | null;
  /** Uang sudah bergerak (paid_qty > 0 / punya baris nota aktif). */
  locked: boolean;
  /**
   * Baris ber-VOUCHER (mig 5610): `unitPrice`-nya = harga umum − voucher, lalu
   * tier — selalu di bawah harga level, dan itu BENAR. Pengoreksi tak boleh
   * menyentuhnya (arah mana pun). Absen = tak diketahui = dianggap tanpa voucher.
   */
  voucherUsageId?: string | null;
  voucherName?: string | null;
  /**
   * DISKON MANUAL baris (`order_items.manual_discount_mode`, mig 7060 — Ubah
   * Diskon keranjang admin). Harganya = Harga Jual − diskon yang DISEPAKATI
   * admin; harga level hari ini tak menjelaskannya, jadi pengoreksi tak boleh
   * menyentuhnya (arah mana pun) — tanpa ini diskon manual yang lebih kecil
   * dari diskon level "dikoreksi" turun diam-diam saat Buat Nota.
   */
  manualDiscountMode?: string | null;
  /**
   * Baris BUNDEL (`order_items.order_bundle_id`, mig 8040). Harganya = alokasi
   * harga bundel — di bawah harga level, dan itu BENAR; hanya RPC checkout
   * bundel yang menulisnya. Pengoreksi tak boleh menyentuhnya (arah mana pun).
   * WAJIB (bukan opsional, beda dari voucher): typecheck memaksa SETIAP
   * pemanggil memuat kolomnya — pemanggil yang lupa = harga bundel "dikoreksi".
   */
  orderBundleId: string | null;
  /**
   * Peran baris dalam PEMBELIAN BERHADIAH (`order_items.gift_role`, mig 8680).
   * Baris `'hadiah'` = gratis Rp 0 yang SAH — `isEmptyUnitPrice` membacanya
   * "belum berharga", dan DB menolak harga lain di baris itu (trigger
   * `freeze_gift_row_price`: satu baris hadiah menggagalkan seluruh tulisan).
   * Pengoreksi tak boleh menyentuhnya. WAJIB (idiom `orderBundleId`): pemanggil
   * yang lupa = hadiah "diisi" harga level.
   */
  giftRole: string | null;
  /**
   * JEJAK PROMO (`order_items.promo_campaign_name`, mig 3470/3650) — bughunt
   * D16-1. Harga promo terkunci saat pesan; kebijakan `replace` sengaja
   * membuatnya bisa LEBIH MAHAL dari harga level hari ini (putusan #1
   * `promo.ts`), jadi pengoreksi yang "hanya menurunkan" pun salah di sini:
   * harga level menimpa harga promo yang dilihat pembeli saat checkout.
   * WAJIB (idiom `orderBundleId`): pemanggil yang lupa = promo "dikoreksi".
   */
  promoCampaignName: string | null;
  /** Mode potongan promo dari jejak (`promo_discount_mode`); absen = tak diketahui. */
  promoDiscountMode?: string | null;
  /**
   * Baris nota `product_item` AKTIF item ini — WAJIB untuk scope 'empty'
   * (`billedLinesAgree`). `[]` = belum ternota. `null`/absen = TIDAK TAHU
   * (nota gagal dibaca / pemanggil tak memeriksanya) → tidak ditulis.
   */
  billedLines?: readonly BilledLine[] | null;
  /** Tambahan yang termasuk di `resolved` (mig 7900); absen = tak dinilai terhadap nota. */
  resolvedExtra?: number;
};

/** Baris 'empty' yang hasil katalognya membantah notanya (atau notanya tak terbaca). */
export function billedConflict(
  c: Pick<BackfillCandidate, "resolved" | "billedLines" | "resolvedExtra">
): boolean {
  if (c.resolved === null) return false;
  return !Array.isArray(c.billedLines) || !billedLinesAgree(c.resolved, c.billedLines, c.resolvedExtra);
}

/** Baris yang harganya dibentuk voucher — dikecualikan dari SEMUA pengoreksi. */
export function hasVoucherTrace(c: Pick<BackfillCandidate, "voucherUsageId" | "voucherName">): boolean {
  return Boolean(c.voucherUsageId) || Boolean((c.voucherName ?? "").trim());
}

/** Baris ber-diskon manual (mig 7060) — dikecualikan dari SEMUA pengoreksi. */
export function hasManualDiscount(c: Pick<BackfillCandidate, "manualDiscountMode">): boolean {
  return Boolean((c.manualDiscountMode ?? "").trim());
}

/** Baris bundel (mig 8040) — dikecualikan dari SEMUA pengoreksi. */
export function hasBundleTrace(c: Pick<BackfillCandidate, "orderBundleId">): boolean {
  return Boolean((c.orderBundleId ?? "").trim());
}

/** Baris HADIAH pembelian (mig 8680, Rp 0 beku) — dikecualikan dari SEMUA pengoreksi. */
export function hasGiftTrace(c: Pick<BackfillCandidate, "giftRole">): boolean {
  return c.giftRole === "hadiah";
}

/**
 * Baris berjejak PROMO (bughunt D16-1) — dikecualikan dari SEMUA pengoreksi.
 * Harga level dihitung TANPA promo (`levelPrice.ts`), jadi ia tak pernah bisa
 * menjelaskan harga promo; nama kampanye ATAU mode potongannya cukup (kampanye
 * yang dihapus bisa meninggalkan modenya saja).
 */
export function hasPromoTrace(
  c: Pick<BackfillCandidate, "promoCampaignName" | "promoDiscountMode">
): boolean {
  return Boolean((c.promoCampaignName ?? "").trim()) || Boolean((c.promoDiscountMode ?? "").trim());
}

/**
 * Baris yang BENAR-BENAR boleh ditulis pada sebuah scope — satu-satunya
 * penjawab pertanyaan itu (dipakai dry-run & apply supaya laporan dan tulisan
 * tak pernah menyimpang).
 *
 * Pada 'mispriced' hanya baris KEMAHALAN yang lolos: `resolved < unitPrice`.
 * Menaikkan harga DILARANG — snapshot yang lebih murah dari setelan hari ini
 * adalah harga yang benar-benar dilihat pembeli saat memesan (promo terkunci
 * mig 3470, level yang sejak itu berubah, harga nego admin), persis yang dijaga
 * `lineHasSettledDiscount` di editor nota.
 */
export function writableCandidates<T extends BackfillCandidate>(
  candidates: T[],
  scope: BackfillScope
): T[] {
  if (scope === "empty") {
    // Baris ber-voucher DIKECUALIKAN juga di sini (audit 2026-09-10): sejak
    // plafon persen boleh kosong (mig 5660) voucher 100 % — atau nominal ≥
    // harga baris — menghasilkan `unit_price = 0` yang SAH, dan `isEmptyUnitPrice`
    // membacanya sebagai "belum berharga". Mengisinya = potongan voucher hilang
    // sementara jejaknya tetap terpasang: pembeli ditagih penuh, laporan tetap
    // mengklaim potongan.
    // Diskon manual 100 % / nominal = Harga Jual juga menghasilkan Rp 0 yang
    // SAH (mig 7060) — sama seperti voucher, bukan "belum berharga".
    // Baris BUNDEL (mig 8040) juga dikecualikan: harga alokasinya bukan
    // turunan harga level hari ini. Baris HADIAH (mig 8680): Rp 0 yang sah.
    // Baris yang SUDAH ternota hanya diisi bila notanya menagih angka yang
    // sama (`billedLinesAgree`, putusan pemilik 2026-10-20); nota tak terbaca
    // = tidak tahu = tidak ditulis.
    return candidates.filter(
      (c) =>
        c.resolved !== null &&
        isEmptyUnitPrice(c.unitPrice) &&
        !hasVoucherTrace(c) &&
        !hasManualDiscount(c) &&
        !hasBundleTrace(c) &&
        !hasGiftTrace(c) &&
        !hasPromoTrace(c) &&
        !billedConflict(c)
    );
  }
  return candidates.filter(
    (c) =>
      c.resolved !== null &&
      !c.locked &&
      !hasVoucherTrace(c) &&
      !hasManualDiscount(c) &&
      !hasBundleTrace(c) &&
      !hasGiftTrace(c) &&
      !hasPromoTrace(c) &&
      !isEmptyUnitPrice(c.unitPrice) &&
      c.resolved < Number(c.unitPrice)
  );
}

// ─── Produk manual (keranjang admin & impor "Produk Manual") ──────────────────
//
// PRODUK MANUAL = barang yang belum ada di katalog tapi sudah dipesan reseller.
// RPC-nya (`admin_create_manual_product_order` / `admin_upsert_manual_cart_order`)
// membuat produk tersembunyi + varian "Standar" ber-`price_normal` = angka yang
// diketik admin di kolom **"Harga Jual"**, lalu — inilah bug uangnya — MENYALIN
// angka yang SAMA ke `order_items.unit_price`. `price_normal` memang harga
// NORMAL; `unit_price` adalah harga SETELAH diskon level pembeli. Menyamakan
// keduanya berarti pembeli ber-level ditagih harga penuh di halaman pesanannya,
// sementara notanya (dibuat lewat `levelPriceFixes` → pricing.ts) menagih harga
// level — persis selisih order-vs-nota yang terlihat di PROD.
//
// Tanpa Pengaturan Diskon, varian "Standar" itu lahir TANPA satu pun kolom
// diskon per-slot terisi, jadi jalurnya adalah cabang "slot kosong → diskon
// persen grup" di `pricing.ts`. Dengan Pengaturan Diskon (mig 6360) kolom yang
// SAMA diisi — dan varian sintetis di bawah membawa angka yang sama dengan yang
// ditulis RPC ke varian. Helper ini tidak menuliskan aturan harga sendiri: ia
// membangun varian sintetis lalu menyerahkannya ke `getVariantPrice`, supaya
// produk manual mustahil menyimpang dari jalur harga lain (checkout, admin
// tambah produk, edit PO, keranjang admin yang membaca varian aslinya).
//
// Promo SENGAJA tidak ikut: produk manual tersembunyi (`is_active = false`) dan
// tak pernah masuk kampanye mana pun; menerapkan promo yang kebetulan berjalan
// akan mengarang potongan yang tak pernah disepakati.

/** Varian sintetis produk manual: harga normal + (opsional) diskon per-slot. */
function manualVariantPricing(
  priceNormal: number,
  discount: ManualDiscount | null | undefined
): ProductVariation {
  return {
    id: "manual",
    name: "Standar",
    sku: "Standar",
    optionIds: [],
    price_normal: priceNormal,
    ...manualDiscountVariantFields(discount)
  };
}

/**
 * Harga satuan yang boleh masuk `order_items.unit_price` untuk satu produk
 * manual, dari "Harga Jual" yang diketik admin + Pengaturan Diskon produknya
 * (mig 6360; null = tanpa diskon khusus) + level pembeli.
 *
 * `discount` WAJIB sama dengan yang ditulis ke varian — pemanggil mengirim
 * objek yang sama ke RPC (`manualDiscountRpcParams`).
 *
 * `null` = tak ada angka yang sah untuk kolom uang:
 *   · harga dikosongkan admin ("harga menyusul", alur sah — nominalnya diisi
 *     saat Buat Nota / saat harga produknya dilengkapi lewat re-snapshot);
 *   · angka tak terbaca.
 * Pemanggil membiarkan `unit_price` NULL — JANGAN jatuh ke harga penuh.
 */
export function manualProductUnitPrice(
  priceNormal: number | string | null | undefined,
  group: CustomerGroup,
  discount?: ManualDiscount | null
): number | null {
  const base = numOrUndefined(priceNormal);
  if (base === undefined) return null;
  const price = getVariantPrice(manualVariantPricing(base, discount), group);
  return price === undefined ? null : price;
}

/**
 * PETA harga per grup untuk Atur Harga produk Non Web (RPC
 * `admin_set_manual_product_price`, mig 6370): {"umum": n, "<slot>": n, …}.
 * SQL tak menghitung harga — ia hanya memasangkan angka dari peta ini ke
 * pesanan menurut grup AKTIF pembelinya. Tiap angka keluar dari
 * `manualProductUnitPrice`, fungsi yang sama dengan harga saat pesanan
 * dibuat, jadi jalur "harga menyusul" tak bisa menyimpang dari jalur biasa.
 *
 * `groups` = grup AKTIF toko (yang juga dipakai kolom diskonnya); grup yang
 * tak ikut di sini membuat pesanan pembelinya dilewati RPC, bukan ditebak.
 */
export function manualProductGroupPrices(
  priceNormal: number,
  groups: ReadonlyArray<{ id: string; slot: number; name: string; discountPersen: number }>,
  discount: ManualDiscount | null
): Record<string, number> {
  const out: Record<string, number> = {};
  const umum = manualProductUnitPrice(priceNormal, null, discount);
  if (umum !== null) out.umum = umum;
  for (const g of groups) {
    const price = manualProductUnitPrice(
      priceNormal,
      {
        id: g.id,
        slot: g.slot,
        name: g.name,
        discountPersen: g.discountPersen,
        isActive: true
      },
      discount
    );
    if (price !== null) out[String(g.slot)] = price;
  }
  return out;
}
